import React, {ReactElement, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import {ColumnGroup} from 'react-table';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Form, useForm} from 'components/ui-kit/Form';

import {ButtonsContainer} from './styled';

export type FiltersType = Array<{id: string; value: any}>;

interface ColumnProps {
    column: {id: string; filterValue?: string; setFilter: Function};
}

const Wrapper = (
    filter: ReactElement,
    {column}: ColumnProps,
    filters: FiltersType,
    setFilters: (filters: FiltersType) => void,
) => {
    const {t} = useTranslation();

    const formData = useForm({mode: 'onChange'});
    const isFormValid = () =>
        formData.formState.isDirty && Object.keys(formData.errors).length === 0;

    const fieldsConfig = useMemo(() => {
        const defaultValue = () => {
            const filter = filters.find((filter) => filter.id === column.id);
            return filter ? filter.value : null;
        };

        return {
            wrappedField: {
                defaultValue: defaultValue(),
                validation: {
                    required: true,
                },
            },
        };
    }, [filters, column.id]);

    const onSubmit = (data: Record<string, any>) => {
        const index = filters.findIndex((filter) => filter.id === column.id);

        if (index === -1) {
            setFilters([...filters, {id: column.id, value: data.wrappedField}]);
        } else {
            const newFilters = [...filters];
            newFilters[index] = {id: column.id, value: data.wrappedField};
            setFilters(newFilters);
        }
    };

    const onReset = () => {
        const index = filters.findIndex((filter) => filter.id === column.id);
        if (index !== -1) {
            const newFilters = [...filters];
            newFilters.splice(index, 1);
            setFilters(newFilters);
        }
    };

    return (
        <Form
            formData={formData}
            onSubmit={onSubmit}
            fieldsConfig={fieldsConfig}
            maxFieldsDepth={2}
        >
            {React.cloneElement(filter, {...filter.props, formFieldName: 'wrappedField'})}

            <ButtonsContainer>
                <Button variant={ButtonVariant.Secondary} type="button" onClick={onReset} size="sm">
                    {t('components.table.Reset')}
                </Button>
                <Button
                    variant={ButtonVariant.Normal}
                    type="submit"
                    disabled={!isFormValid()}
                    size="sm"
                >
                    {t('components.table.Submit')}
                </Button>
            </ButtonsContainer>
        </Form>
    );
};

const FilterWrapper = (
    filter: ReactElement,
    filters: FiltersType,
    onChange: (filters: FiltersType) => void,
) => (column: ColumnProps) => Wrapper(filter, column, filters, onChange);

export const mapTableFilters = (
    columns: ColumnGroup<object>[],
    filters: FiltersType,
    onChange: (filters: FiltersType) => void,
): ColumnGroup<object>[] =>
    columns.map((elem) => {
        if (elem.columns)
            elem.columns = mapTableFilters(elem.columns as ColumnGroup[], filters, onChange);
        return {
            ...elem,
            Filter: elem.Filter
                ? FilterWrapper(elem.Filter as ReactElement, filters, onChange)
                : undefined,
        };
    });
